import React, { useCallback, useEffect, useMemo, useState } from "react";
import apiFetch from "../../../context/api";
import Button from "../../Elements/Button";

function today() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function formatCurrency(value) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}
function formatCompact(v) {
  return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(Number(v || 0));
}

export default function CajasStats({ token, venueID }) {
  const [fromDate, setFromDate] = useState(daysAgo(30));
  const [toDate, setToDate]     = useState(today());
  const [applied, setApplied]   = useState({ from: daysAgo(30), to: today() });
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async (from, to) => {
    setLoading(true);
    setError("");
    try {
      const q = new URLSearchParams({ from_date: from, to_date: to });
      if (venueID) q.set("venue_id", venueID);
      const res = await apiFetch(`/v1/reports/cash?${q}`, { headers: authHeaders });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message || "Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, venueID]);

  useEffect(() => {
    if (token) fetchData(applied.from, applied.to);
  }, [token, applied, fetchData]);

  function handleFilter() {
    setApplied({ from: fromDate, to: toDate });
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Date filter bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <span className="text-sm font-medium text-gray-500 mr-1">Período</span>
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-gray-400">Desde</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-sm text-gray-800 focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)]"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-gray-400">Hasta</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-sm text-gray-800 focus:border-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)]"
          />
        </div>
        <Button onClick={handleFilter}>
          Filtrar
        </Button>

        {/* Summary pills */}
        {!loading && data && (
          <div className="ml-auto flex items-center gap-3 text-xs text-gray-500">
            <span>
              <span className="font-semibold text-gray-800">{formatCompact(data.totals?.total_boxes)}</span> cajas totales
            </span>
            <span>
              <span className="font-semibold text-emerald-600">{formatCompact(data.totals?.open_boxes)}</span> abiertas
            </span>
            <span>
              <span className="font-semibold text-gray-800">{formatCompact(data.totals?.closed_sessions)}</span> sesiones cerradas
            </span>
          </div>
        )}
      </div>

      {loading && (
        <div className="text-center py-16 text-gray-400 text-sm">Cargando datos…</div>
      )}
      {error && (
        <div className="text-center py-10 text-red-500 text-sm">{error}</div>
      )}

      {!loading && !error && (
        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Estado de cajas</h3>
            <p className="mt-0.5 text-sm text-gray-500">
              Sesiones registradas del {applied.from} al {applied.to}
            </p>
          </div>

          {(data?.boxes || []).length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">Sin cajas registradas en este período.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {(data?.boxes || []).map((box) => (
                <div key={box.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-gray-800 text-sm">{box.name}</div>
                      <div className="text-xs text-gray-500">{box.zone || "Sin zona"}</div>
                    </div>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold shrink-0 ${
                        box.has_open_session
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {box.has_open_session ? "Abierta" : "Cerrada"}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1 text-xs text-gray-600">
                    <div>Saldo por defecto: {formatCurrency(box.default_opening_balance)}</div>
                    <div>Sesiones: {formatCompact(box.session_count)}</div>
                    <div>
                      Último mov.:{" "}
                      {box.last_session_at
                        ? new Date(box.last_session_at).toLocaleDateString("es-ES")
                        : "—"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
